import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './CollectionsManager.css';
import ImageInput from './ImageInput';

const CollectionsManager = () => {
  const { authenticatedFetch } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: '',
    published: false,
    sortOrder: 0
  });

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await authenticatedFetch('http://localhost:5000/api/collections/admin/all');
      
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setCollections(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to load collections' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    
    // Auto-generate slug from name
    if (e.target.name === 'name' && !editing) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editing 
        ? `http://localhost:5000/api/collections/${editing}`
        : 'http://localhost:5000/api/collections';
      
      const response = await authenticatedFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      setMessage({ type: 'success', text: editing ? 'Collection updated!' : 'Collection created!' });
      resetForm();
      fetchCollections();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save collection' });
    }
  };

  const handleEdit = (collection) => {
    setFormData(collection);
    setEditing(collection.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this collection?')) return;
    
    try {
      const response = await authenticatedFetch(`http://localhost:5000/api/collections/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      setMessage({ type: 'success', text: 'Collection deleted!' });
      fetchCollections();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete collection' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      image: '',
      published: false,
      sortOrder: 0
    });
    setEditing(null);
    setShowForm(false);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="collections-manager">
      <div className="manager-header">
        <h2>Manage Collections</h2>
        <button className="add-button" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Collection'}
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="collection-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Slug *</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <ImageInput label="Image" value={formData.image} onChange={(v) => setFormData(prev => ({ ...prev, image: v }))} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleChange}
              />
              <span>Published</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={resetForm} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="save-button">
              {editing ? 'Update' : 'Create'} Collection
            </button>
          </div>
        </form>
      )}

      <div className="collections-list">
        {collections.length === 0 ? (
          <p className="empty-state">No collections yet. Create your first one!</p>
        ) : (
          collections.map(collection => (
            <div key={collection.id} className="collection-card">
              {collection.image && (
                <div className="collection-image">
                  <img src={collection.image} alt={collection.name} />
                </div>
              )}
              <div className="collection-info">
                <h3>{collection.name}</h3>
                <p className="collection-slug">{collection.slug}</p>
                {collection.description && (
                  <p className="collection-description">{collection.description}</p>
                )}
                <div className="collection-meta">
                  <span className={`status ${collection.published ? 'published' : 'draft'}`}>
                    {collection.published ? 'Published' : 'Draft'}
                  </span>
                  <span className="product-count">
                    {collection._count.products} products
                  </span>
                </div>
              </div>
              <div className="collection-actions">
                <button onClick={() => handleEdit(collection)} className="edit-btn">
                  Edit
                </button>
                <button onClick={() => handleDelete(collection.id)} className="delete-btn">
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CollectionsManager;
